import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useTranslation } from 'react-i18next';
import {
  Play,
  Mail,
  MessageSquare,
  Phone,
  Bell,
  Webhook,
  Link2,
} from 'lucide-react';

interface ActionNodeData {
  label: string;
  type: string;
  icon?: string;
  config: Record<string, any>;
  isConfigured?: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  Mail: <Mail className="w-5 h-5" />,
  MessageSquare: <MessageSquare className="w-5 h-5" />,
  Phone: <Phone className="w-5 h-5" />,
  Bell: <Bell className="w-5 h-5" />,
  Webhook: <Webhook className="w-5 h-5" />,
  Link2: <Link2 className="w-5 h-5" />,
};

const ActionNode: React.FC<NodeProps<ActionNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();

  const getConfigPreview = () => {
    if (data.type === 'send_email' && data.config?.subject) {
      return `${t('workflow.subject', 'Assunto')}: ${data.config.subject.substring(0, 30)}...`;
    }
    if ((data.type === 'send_whatsapp' || data.type === 'send_sms') && data.config?.message) {
      return `${t('workflow.msg', 'Msg')}: ${data.config.message.substring(0, 30)}...`;
    }
    if (data.type === 'redirect' && (data.config?.targetLpId || data.config?.externalUrl)) {
      return `${t('workflow.to', 'Para')}: ${data.config.targetLpId || data.config.externalUrl}`;
    }
    return null;
  };

  const configPreview = getConfigPreview();

  return (
    <div
      className={`relative min-w-[180px] bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all ${
        selected
          ? 'border-blue-500 shadow-blue-100 dark:shadow-blue-900/30'
          : 'border-blue-200 dark:border-blue-800 hover:border-blue-400'
      }`}
    >
      {/* Handle - Input */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-blue-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-lg px-4 py-2">
        <div className="flex items-center gap-2 text-white">
          <Play className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">{t('workflow.action', 'Acao')}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-400">
            {iconMap[data.icon || 'Play'] || <Play className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label}
            </p>
            {data.isConfigured ? (
              <span className="text-xs text-blue-600 dark:text-blue-400">{t('workflow.configured', 'Configurado')}</span>
            ) : (
              <span className="text-xs text-yellow-600 dark:text-yellow-400">{t('workflow.clickToConfigure', 'Clique para configurar')}</span>
            )}
          </div>
        </div>

        {/* Config Preview */}
        {configPreview && (
          <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {configPreview}
            </p>
          </div>
        )}
      </div>

      {/* Handle - Output */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-4 !h-4 !bg-blue-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />
    </div>
  );
};

export default memo(ActionNode);
